"use client";
import { useEffect, useMemo, useState } from "react";
import { ClinicRow } from "../../types/Clinic";
import { ProviderRow } from "../../types/Provider";
import { apiFetch } from "../../lib/api-client/base";
import { publicRoutes } from "../../lib/api-client/publicRoutes";
import { ClinicHours } from "../../types/ClinicHours";
import { DAY_ORDER, DAY_LABELS, SKDAYS_LC } from "../../lib/days";
import { CLINIC_CSS_THEMES } from "../../lib/themes";
import { DateTime } from "luxon";
import { getProviderDisplayName } from "../../lib/providerName";
import {
  formatNextSlot,
  parseAnnouncementDateDisplay,
  sortAnnouncementsNewestFirst,
} from "../../lib/date-time";
import { getSpecializationLabel } from "../../lib/specializations";

function parseAbsenceRange(range: string): { start: Date; end: Date } | null {
  const n = (String(range).match(/\d+/g) || []).map(Number);
  if (n.length >= 5)
    return {
      start: new Date(n[4], n[1] - 1, n[0]),
      end: new Date(n[4], n[3] - 1, n[2]),
    };
  if (n.length === 4)
    return {
      start: new Date(n[3], n[2] - 1, n[0]),
      end: new Date(n[3], n[2] - 1, n[1]),
    };
  if (n.length === 3)
    return {
      start: new Date(n[2], n[1] - 1, n[0]),
      end: new Date(n[2], n[1] - 1, n[0]),
    };
  return null;
}

export default function ClinicProfile(props: {
  clinic: ClinicRow;
  providers: ProviderRow[];
  loadingProviders?: boolean;
  onContinue: (providerUrl: string) => void;
}) {
  const { clinic, providers } = props;
  const theme = clinic.theme || "blue";
  const t = CLINIC_CSS_THEMES[theme] || CLINIC_CSS_THEMES.blue;

  const [selectedProviderId, setSelectedProviderId] = useState<string>(
    providers.length === 1 ? providers[0].id : ""
  );
  const [clinicHours, setClinicHours] = useState<ClinicHours[]>([]);
  const [blockTimeAbsences, setBlockTimeAbsences] = useState<
    {
      id: number;
      title: string;
      start_utc: string;
      end_utc: string;
      all_day: boolean;
      message?: string;
      providerName: string | null;
    }[]
  >([]);
  const [totalProviders, setTotalProviders] = useState(1);

  useEffect(() => {
    if (!clinic.id) return;
    apiFetch(publicRoutes.clinicHours.get(clinic.id))
      .then((r) => setClinicHours(r.hours || []))
      .catch(() => {});
  }, [clinic.id]);

  useEffect(() => {
    if (!clinic.id) return;
    fetch(`/api/public/clinic-absences?clinicId=${clinic.id}`)
      .then((res) => res.json())
      .then((r) => {
        setBlockTimeAbsences(r.items || []);
        setTotalProviders(r.totalProviders ?? 1);
      })

      .catch(() => {});
  }, [clinic.id]);

  // ── derived ──────────────────────────────────────────────────────────
  const hasLogo = !!clinic.logo_url?.trim();
  const initial = ((clinic.name || "?").trim().charAt(0) || "?").toUpperCase();
  const hasHeroPhoto = !!clinic.hero_photo_url?.trim();
  const heroVariant = hasHeroPhoto ? "split" : "center";

  const today = new Date();
  const dowToday = (today.getDay() + 6) % 7;
  const todayDayName = SKDAYS_LC[dowToday];

  const hasAnyActiveBlock = blockTimeAbsences.some((bt) => {
    const start = new Date(bt.start_utc);
    const end = new Date(bt.end_utc);
    return today >= start && today <= end;
  });

  const announcements = sortAnnouncementsNewestFirst(
    clinic.announcements || []
  ).map((a) => ({
    ...a,
    ...parseAnnouncementDateDisplay(a.date),
  }));

  // hours
  const formattedHours = DAY_ORDER.map((day) => {
    const dayHours = clinicHours.filter((h) => h.day_of_week === day);
    if (dayHours.length === 0 && (day === "saturday" || day === "sunday"))
      return null;
    const skName = DAY_LABELS[day] || day;
    const skLower = skName.toLowerCase();
    let isToday = false;
    if (dowToday < 5) isToday = skLower.indexOf(todayDayName) >= 0;
    else
      isToday =
        skLower.indexOf("sobota") >= 0 ||
        skLower.indexOf("nedeľa") >= 0 ||
        skLower.indexOf(todayDayName) >= 0;
    const time =
      dayHours.length === 0
        ? "Zatvorené"
        : dayHours
            .map(
              (h) =>
                `${DateTime.fromISO(h.open_time).toFormat("HH:mm")} – ${DateTime.fromISO(h.close_time).toFormat("HH:mm")}`
            )
            .join(", ");
    return { day: skName, time, isToday };
  }).filter(Boolean) as { day: string; time: string; isToday: boolean }[];

  // "nearest slot" is shown only for the picked provider
  const selectedProvider = providers.find((p) => p.id === selectedProviderId);

  // CSS vars as inline style
  const wrapStyle: React.CSSProperties = {
    ...Object.entries(t).reduce((acc, [k, v]) => {
      (acc as Record<string, string>)[k] = v;
      return acc;
    }, {} as React.CSSProperties),
    position: "relative",
    overflow: "hidden",
    width: "100%",
    fontFamily: "'Plus Jakarta Sans', sans-serif",
    color: "var(--ink)",
    background: "var(--bg)",
    minHeight: "100vh",
  };

  function handleProviderClick(provider: ProviderRow) {
    setSelectedProviderId(provider.id);
  }

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        rel="preconnect"
        href="https://fonts.gstatic.com"
        crossOrigin="anonymous"
      />
      <link
        href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />
      <style>{`
        .cp-wrap { box-sizing: border-box; }
        .cp-wrap *, .cp-wrap *::before, .cp-wrap *::after { box-sizing: border-box; }
        .cp-inner { max-width: 1100px; margin: 0 auto; padding: 0 clamp(16px, 5vw, 48px); position: relative; z-index: 1; }
        .cp-hero-split { display: flex; flex-wrap: wrap; gap: clamp(20px,4%,32px); align-items: center; padding: clamp(20px,4%,32px) 0; }
        .cp-hero-split-text { flex: 1 1 260px; min-width: 0; }
        .cp-hero-split-img { flex: 1 1 220px; min-width: 0; aspect-ratio: 4/3; border-radius: 24px; overflow: hidden; }
        .cp-info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; }
        .cp-provider-btn { width: 100%; text-align: left; display: flex; align-items: center; gap: 12px; border-radius: 16px; padding: 14px; cursor: pointer; font-family: inherit; border-width: 2px; border-style: solid; }
        .cp-provider-name { display: block; font-weight: 700; font-size: 16px; line-height: 1.2; }
        .cp-provider-role { display: block; font-size: 13px; margin-top: 2px; }
        .cp-absence-items { display: flex; flex-direction: column; gap: 12px; }
        .cp-announcements-list { display: flex; flex-direction: column; gap: 12px; }
        .cp-footer { display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap; }

        @media (max-width: 480px) {
          .cp-hero-split-img { min-height: 180px; }
          .cp-provider-btn { padding: 12px; gap: 10px; }
          .cp-provider-name { font-size: 15px; }
          .cp-provider-avatar { width: 46px !important; height: 46px !important; font-size: 16px !important; }
          .cp-booking-cta { font-size: 15px !important; padding: 14px !important; }
          .cp-info-grid { grid-template-columns: 1fr; }
          .cp-footer { flex-direction: column; align-items: flex-start; gap: 8px; }
        }
      `}</style>

      <div style={wrapStyle} className="cp-wrap">
        {/* decorative background */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(60% 50% at 4% -6%, var(--accent) 0%, transparent 58%), radial-gradient(52% 42% at 106% 6%, var(--accent) 0%, transparent 60%), radial-gradient(68% 52% at 98% 102%, var(--accent) 0%, transparent 62%)",
            opacity: 0.28,
          }}
        />
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            backgroundImage:
              "radial-gradient(var(--accent) 1.5px, transparent 1.6px)",
            backgroundSize: "22px 22px",
            opacity: 0.18,
            WebkitMaskImage:
              "radial-gradient(140% 100% at 100% 0%, #000 0%, transparent 65%)",
            maskImage:
              "radial-gradient(140% 100% at 100% 0%, #000 0%, transparent 65%)",
          }}
        />

        <div className="cp-inner">
          {/* TOP BAR */}
          <header
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "18px 0 16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                minWidth: 0,
              }}
            >
              {hasLogo ? (
                <img
                  src={clinic.logo_url!}
                  alt="Logo"
                  style={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    borderRadius: 12,
                    objectFit: "contain",
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                  }}
                />
              ) : (
                <span
                  style={{
                    width: 42,
                    height: 42,
                    flexShrink: 0,
                    borderRadius: 12,
                    background: "var(--accent)",
                    color: "var(--accent-ink)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 800,
                    fontSize: 19,
                  }}
                >
                  {initial}
                </span>
              )}
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 16,
                    lineHeight: 1.1,
                    letterSpacing: "-0.01em",
                    color: "var(--ink)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {clinic.name}
                </div>
                {clinic.city && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--ink-mute)",
                      marginTop: 2,
                    }}
                  >
                    {clinic.city}
                  </div>
                )}
              </div>
            </div>
            <div
              style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
            >
              <a
                href="https://ordinex.sk"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/ordinex-logo.svg"
                  alt="Ordinex"
                  style={{ height: 60, width: "auto", display: "block" }}
                />
              </a>
            </div>
          </header>

          {/* HERO */}
          {heroVariant === "center" ? (
            <section
              style={{
                textAlign: "center",
                padding: "clamp(30px,7%,56px) 0 clamp(20px,4%,32px)",
              }}
            >
              <div
                style={{
                  display: "inline-block",
                  background: "var(--accent-tint)",
                  color: "var(--accent)",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: 20,
                  padding: "5px 14px",
                  marginBottom: 14,
                }}
              >
                Zdravotná starostlivosť
              </div>
              <h1
                style={{
                  fontWeight: 800,
                  fontSize: "clamp(30px,8vw,46px)",
                  lineHeight: 1.1,
                  letterSpacing: "-0.025em",
                  margin: "0 0 0",
                  color: "var(--ink)",
                }}
              >
                Vitajte v ambulancii
                <br />
                {clinic.name}
              </h1>
              {clinic.tagline && (
                <p
                  style={{
                    fontSize: "clamp(15px,4vw,17px)",
                    lineHeight: 1.65,
                    color: "var(--ink-soft)",
                    maxWidth: 520,
                    margin: "16px auto 0",
                  }}
                >
                  {clinic.tagline}
                </p>
              )}
            </section>
          ) : (
            <section className="cp-hero-split">
              <div className="cp-hero-split-text">
                <div
                  style={{
                    display: "inline-block",
                    background: "var(--accent-tint)",
                    color: "var(--accent)",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: 20,
                    padding: "5px 14px",
                    marginBottom: 14,
                  }}
                >
                  Zdravotná starostlivosť
                </div>
                <h1
                  style={{
                    fontWeight: 800,
                    fontSize: "clamp(28px,7vw,42px)",
                    lineHeight: 1.1,
                    letterSpacing: "-0.025em",
                    margin: 0,
                    color: "var(--ink)",
                  }}
                >
                  Vitajte v ambulancii {clinic.name}
                </h1>
                {clinic.tagline && (
                  <p
                    style={{
                      fontSize: "clamp(15px,4vw,17px)",
                      lineHeight: 1.65,
                      color: "var(--ink-soft)",
                      margin: "16px 0 0",
                    }}
                  >
                    {clinic.tagline}
                  </p>
                )}
              </div>
              <div
                className="cp-hero-split-img"
                style={{
                  boxShadow: "0 18px 40px -22px var(--shadow)",
                  background: "var(--surface-2)",
                  border: "1px solid var(--line)",
                }}
              >
                {hasHeroPhoto ? (
                  <img
                    src={clinic.hero_photo_url!}
                    alt="Foto ambulancie"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 9,
                      color: "var(--ink-mute)",
                    }}
                  >
                    <svg
                      width="30"
                      height="30"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="3" />
                      <circle cx="9" cy="9" r="2" />
                      <path d="m21 15-5-5L5 21" />
                    </svg>
                    <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                      Foto ambulancie
                    </span>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* CLOSURE BANNER — shown when there's an active block time */}
          {hasAnyActiveBlock && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 11,
                borderRadius: 16,
                padding: "15px 17px",
                marginTop: 16,
                background: "var(--warn-tint)",
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  flexShrink: 0,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  background: "var(--warn)",
                }}
              >
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <path d="M12 9v4M12 17h.01" />
                </svg>
              </span>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 14.5,
                  color: "var(--warn)",
                }}
              >
                {totalProviders > 1
                  ? "Niektorí odborníci sú momentálne neprítomní"
                  : "Ambulancia je momentálne nedostupná"}
              </div>
            </div>
          )}

          {/* BOOKING CARD */}
          <section
            style={{
              background: "var(--surface)",
              border: "1px solid var(--line)",
              borderRadius: 22,
              padding: "clamp(20px,4%,26px)",
              boxShadow:
                "0 2px 4px var(--shadow),0 22px 44px -28px var(--shadow)",
              marginTop: 8,
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--accent)",
                marginBottom: 5,
              }}
            >
              Rezervácia termínu
            </div>
            <h2
              style={{
                fontWeight: 800,
                fontSize: "clamp(19px,5vw,23px)",
                letterSpacing: "-0.02em",
                margin: "0 0 16px",
                color: "var(--ink)",
              }}
            >
              Vyberte si odborníka
            </h2>

            {providers.length === 0 && (
              <div
                style={{
                  color: "var(--ink-mute)",
                  fontSize: 14,
                  padding: "12px 0",
                }}
              >
                {props.loadingProviders
                  ? "Načítavam..."
                  : "Žiadni odborníci nie sú k dispozícii."}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {providers.map((provider) => {
                const hasPhoto = !!provider.photo_url?.trim();
                const name = getProviderDisplayName(provider);
                const initials =
                  [provider.first_name, provider.last_name]
                    .filter(Boolean)
                    .map((s) => s!.charAt(0).toUpperCase())
                    .join("") || "Dr";
                const isSelected = selectedProviderId === provider.id;
                return (
                  <button
                    key={provider.id}
                    onClick={() => handleProviderClick(provider)}
                    data-testid="provider-card"
                    className="cp-provider-btn"
                    style={{
                      background: isSelected
                        ? "var(--accent-tint)"
                        : "var(--surface)",
                      borderColor: isSelected ? "var(--accent)" : "var(--line)",
                    }}
                  >
                    {hasPhoto ? (
                      <img
                        src={provider.photo_url!}
                        alt={name}
                        className="cp-provider-avatar"
                        style={{
                          width: 54,
                          height: 54,
                          flexShrink: 0,
                          borderRadius: "50%",
                          objectFit: "cover",
                          boxShadow: "0 0 0 3px var(--surface)",
                        }}
                      />
                    ) : (
                      <span
                        className="cp-provider-avatar"
                        style={{
                          width: 54,
                          height: 54,
                          flexShrink: 0,
                          borderRadius: "50%",
                          background: "var(--accent)",
                          color: "var(--accent-ink)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 800,
                          fontSize: 18,
                          boxShadow: "0 0 0 3px var(--surface)",
                        }}
                      >
                        {initials}
                      </span>
                    )}
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                      <span
                        className="cp-provider-name"
                        style={{ color: "var(--ink)" }}
                      >
                        {name}
                      </span>{" "}
                      {provider.specializations &&
                        provider.specializations.length > 0 && (
                          <span
                            className="cp-provider-role"
                            style={{ color: "var(--ink-soft)" }}
                          >
                            {provider.specializations
                              .map(getSpecializationLabel)
                              .join(", ")}
                          </span>
                        )}
                      {/* Only worth reading when there is a choice to make:
                          the description exists to route a patient to the
                          right colleague. A clinic of one has nobody to route
                          between, which is also why Solo cannot write one —
                          and this keeps a description written on Plus from
                          lingering publicly after a downgrade. */}
                      {totalProviders > 1 && provider.expertise_description && (
                        <span
                          style={{
                            display: "block",
                            marginTop: 4,
                            fontSize: 13,
                            lineHeight: 1.5,
                            color: "var(--ink-soft)",
                            whiteSpace: "pre-line",
                          }}
                          data-testid="provider-expertise"
                        >
                          {provider.expertise_description}
                        </span>
                      )}
                    </span>
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        flexShrink: 0,
                        borderRadius: "50%",
                        background: isSelected
                          ? "var(--accent)"
                          : "var(--line)",
                        color: isSelected
                          ? "var(--accent-ink)"
                          : "var(--ink-mute)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isSelected ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            {!selectedProviderId && providers.length > 0 && (
              <p
                style={{
                  textAlign: "center",
                  fontSize: 13,
                  color: "var(--ink-mute)",
                  margin: "8px 0 0",
                }}
              >
                ↑ Vyberte odborníka pre pokračovanie
              </p>
            )}

            {selectedProvider && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 9,
                  background: "var(--surface-2)",
                  borderRadius: 13,
                  padding: "13px 15px",
                  color: "var(--ink-soft)",
                  fontSize: 13.5,
                  fontWeight: 500,
                  marginTop: 14,
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.9"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ color: "var(--accent)", flexShrink: 0 }}
                >
                  <rect x="3" y="4" width="18" height="18" rx="3" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                Najbližší voľný termín &nbsp;
                <strong style={{ color: "var(--ink)", fontWeight: 700 }}>
                  {selectedProvider.next_available_slot
                    ? formatNextSlot(selectedProvider.next_available_slot)
                    : "—"}
                </strong>
              </div>
            )}

            {providers.length >= 1 && (
              <button
                onClick={() => props.onContinue(selectedProviderId)}
                data-testid="clinic-continue-button"
                disabled={!selectedProviderId}
                className="cp-booking-cta"
                style={{
                  marginTop: 16,
                  width: "100%",
                  border: "none",
                  cursor: selectedProviderId ? "pointer" : "not-allowed",
                  background: selectedProviderId
                    ? "var(--accent)"
                    : "var(--line)",
                  color: selectedProviderId
                    ? "var(--accent-ink)"
                    : "var(--ink-mute)",
                  fontFamily: "inherit",
                  fontWeight: 700,
                  fontSize: 16,
                  padding: 16,
                  borderRadius: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 9,
                  opacity: selectedProviderId ? 1 : 0.6,
                }}
              >
                Rezervovať termín
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </button>
            )}
          </section>

          {/* AKTUALITY */}
          {announcements.length > 0 && (
            <section style={{ paddingTop: "clamp(34px,6%,52px)" }}>
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--accent)",
                  marginBottom: 6,
                }}
              >
                Novinky z ambulancie
              </div>
              <h2
                style={{
                  fontWeight: 800,
                  fontSize: "clamp(22px,5.5vw,28px)",
                  letterSpacing: "-0.02em",
                  margin: "0 0 18px",
                  color: "var(--ink)",
                }}
              >
                Aktuality
              </h2>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {announcements.map((a, i) => (
                  <article
                    key={i}
                    style={{
                      display: "flex",
                      gap: 15,
                      background: "var(--surface)",
                      border: "1px solid var(--line)",
                      borderRadius: 18,
                      padding: 18,
                      boxShadow: "0 1px 3px var(--shadow)",
                    }}
                  >
                    <div
                      style={{
                        width: 56,
                        flexShrink: 0,
                        textAlign: "center",
                        background: "var(--accent-tint)",
                        borderRadius: 13,
                        padding: "11px 6px",
                        alignSelf: "flex-start",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 800,
                          fontSize: 17,
                          lineHeight: 1,
                          color: "var(--accent-strong)",
                        }}
                      >
                        {a.day}
                      </div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 10,
                          textTransform: "uppercase",
                          color: "var(--accent)",
                          marginTop: 4,
                        }}
                      >
                        {a.month}
                      </div>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {a.tag && (
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 10.5,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: "var(--ink-mute)",
                            marginBottom: 5,
                          }}
                        >
                          {a.tag}
                        </div>
                      )}
                      <h3
                        style={{
                          fontWeight: 700,
                          fontSize: "clamp(16px,4.2vw,18px)",
                          lineHeight: 1.25,
                          margin: "0 0 5px",
                          color: "var(--ink)",
                        }}
                      >
                        {a.title}
                      </h3>
                      <p
                        style={{
                          margin: 0,
                          color: "var(--ink-soft)",
                          fontSize: 14,
                          lineHeight: 1.6,
                        }}
                      >
                        {a.text}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}

          {/* NEPRÍTOMNOSŤ */}
          <section style={{ paddingTop: "clamp(34px,6%,52px)" }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--accent)",
                marginBottom: 6,
              }}
            >
              Dostupnosť
            </div>
            <h2
              style={{
                fontWeight: 800,
                fontSize: "clamp(22px,5.5vw,28px)",
                letterSpacing: "-0.02em",
                margin: "0 0 16px",
                color: "var(--ink)",
              }}
            >
              Neprítomnosť a dovolenka
            </h2>
            {!hasAnyActiveBlock && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 11,
                  borderRadius: 16,
                  padding: "15px 17px",
                  marginBottom: 16,
                  background: "var(--ok-tint)",
                }}
              >
                <span
                  style={{
                    width: 34,
                    height: 34,
                    flexShrink: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    background: "var(--ok)",
                  }}
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                </span>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 14.5,
                    color: "var(--ok)",
                  }}
                >
                  Ambulancia ordinuje bez obmedzení
                </div>
              </div>
            )}
            {blockTimeAbsences.length > 0 && (
              <div className="cp-absence-items">
                {blockTimeAbsences.map((bt) => {
                  const start = new Date(bt.start_utc);
                  const end = new Date(bt.end_utc);
                  const isActive = today >= start && today <= end;
                  const fmt = (d: Date) =>
                    `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
                  const fmtTime = (d: Date) =>
                    `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                  const sameDay = start.toDateString() === end.toDateString();
                  const rangeLabel = bt.all_day
                    ? sameDay
                      ? fmt(start)
                      : `${fmt(start)} – ${fmt(end)}`
                    : sameDay
                      ? `${fmt(start)}, ${fmtTime(start)} – ${fmtTime(end)}`
                      : `${fmt(start)} ${fmtTime(start)} – ${fmt(end)} ${fmtTime(end)}`;
                  return (
                    <div
                      key={bt.id}
                      style={{
                        display: "flex",
                        gap: 14,
                        background: "var(--surface)",
                        borderRadius: 16,
                        padding: 16,
                        border: `1px solid ${isActive ? "var(--accent)" : "var(--line)"}`,
                      }}
                    >
                      <span
                        style={{
                          width: 38,
                          height: 38,
                          flexShrink: 0,
                          borderRadius: 11,
                          background: "var(--accent-tint)",
                          color: "var(--accent)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.9"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="4" width="18" height="18" rx="3" />
                          <path d="M16 2v4M8 2v4M3 10h18" />
                        </svg>
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {totalProviders > 1 && bt.providerName && (
                          <div
                            style={{
                              fontSize: 14,
                              fontWeight: 600,
                              color: "var(--ink-soft)",
                              marginBottom: 4,
                            }}
                          >
                            {bt.providerName}
                          </div>
                        )}
                        <div
                          className="cp-absence-row"
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 9,
                            flexWrap: "wrap",
                            marginBottom: 4,
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 700,
                              fontSize: 14.5,
                              color: "var(--ink)",
                            }}
                          >
                            {rangeLabel}
                          </span>
                          {isActive && (
                            <span
                              style={{
                                background: "var(--accent-tint)",
                                color: "var(--accent)",
                                fontWeight: 700,
                                fontSize: 10.5,
                                letterSpacing: "0.04em",
                                textTransform: "uppercase",
                                padding: "3px 9px",
                                borderRadius: 999,
                              }}
                            >
                              Prebieha
                            </span>
                          )}
                        </div>
                        {bt.message && (
                          <div
                            style={{
                              color: "var(--ink-soft)",
                              fontSize: 13,
                              lineHeight: 1.55,
                            }}
                          >
                            {bt.message}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* O NÁS */}
          {clinic.description && (
            <section style={{ paddingTop: "clamp(34px,6%,52px)" }}>
              <div
                style={{
                  background: "var(--surface-2)",
                  borderRadius: 22,
                  padding: "clamp(22px,5%,34px)",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 11,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "var(--accent)",
                    marginBottom: 6,
                  }}
                >
                  Kto sme
                </div>
                <h2
                  style={{
                    fontWeight: 800,
                    fontSize: "clamp(22px,5.5vw,28px)",
                    letterSpacing: "-0.02em",
                    margin: "0 0 14px",
                    color: "var(--ink)",
                  }}
                >
                  O nás
                </h2>
                <p
                  style={{
                    margin: 0,
                    color: "var(--ink-soft)",
                    fontSize: 15.5,
                    lineHeight: 1.75,
                  }}
                >
                  {clinic.description}
                </p>
                {clinic.website && (
                  <a
                    href={
                      clinic.website.startsWith("http")
                        ? clinic.website
                        : `https://${clinic.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      marginTop: 16,
                      color: "var(--accent-strong)",
                      fontWeight: 700,
                      fontSize: 14,
                      textDecoration: "none",
                    }}
                  >
                    {clinic.website}
                    <svg
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                  </a>
                )}
              </div>
            </section>
          )}

          {/* INFO GRID */}
          <section style={{ paddingTop: "clamp(34px,6%,52px)" }}>
            <div
              style={{
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "var(--accent)",
                marginBottom: 6,
              }}
            >
              Kontakt a návšteva
            </div>
            <h2
              style={{
                fontWeight: 800,
                fontSize: "clamp(22px,5.5vw,28px)",
                letterSpacing: "-0.02em",
                margin: "0 0 16px",
                color: "var(--ink)",
              }}
            >
              Kde nás nájdete
            </h2>
            <div className="cp-info-grid">
              {(clinic.street ||
                clinic.city ||
                clinic.email ||
                clinic.phone_number) && (
                <div
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                    borderRadius: 18,
                    padding: "16px 20px",
                    boxShadow: "0 1px 3px var(--shadow)",
                    display: "flex",
                    gap: 20,
                    flexWrap: "wrap",
                  }}
                >
                  {(clinic.street || clinic.city) && (
                    <div style={{ flex: "1 1 120px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          marginBottom: 8,
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ color: "var(--accent)", flexShrink: 0 }}
                        >
                          <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "var(--ink)",
                          }}
                        >
                          Adresa
                        </span>
                      </div>
                      <p
                        style={{
                          margin: 0,
                          color: "var(--ink-soft)",
                          fontSize: 13.5,
                          lineHeight: 1.6,
                        }}
                      >
                        {clinic.street && (
                          <>
                            {clinic.street}
                            <br />
                          </>
                        )}
                        {clinic.city}
                      </p>
                    </div>
                  )}
                  {(clinic.street || clinic.city) &&
                    (clinic.email || clinic.phone_number) && (
                      <div
                        style={{
                          width: 1,
                          alignSelf: "stretch",
                          background: "var(--line)",
                          flexShrink: 0,
                        }}
                      />
                    )}
                  {(clinic.email || clinic.phone_number) && (
                    <div style={{ flex: "1 1 120px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          marginBottom: 8,
                        }}
                      >
                        <svg
                          width="15"
                          height="15"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ color: "var(--accent)", flexShrink: 0 }}
                        >
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92Z" />
                        </svg>
                        <span
                          style={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "var(--ink)",
                          }}
                        >
                          Kontakt
                        </span>
                      </div>
                      {clinic.email && (
                        <p
                          style={{
                            margin: "0 0 4px",
                            color: "var(--ink-soft)",
                            fontSize: 13.5,
                            lineHeight: 1.5,
                            wordBreak: "break-word",
                          }}
                        >
                          {clinic.email}
                        </p>
                      )}
                      {clinic.phone_number && (
                        <p
                          style={{
                            margin: 0,
                            color: "var(--ink)",
                            fontWeight: 700,
                            fontSize: 13.5,
                          }}
                        >
                          {clinic.phone_number}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              {formattedHours.length > 0 && (
                <div
                  style={{
                    background: "var(--surface)",
                    border: "1px solid var(--line)",
                    borderRadius: 18,
                    padding: 20,
                    boxShadow: "0 1px 3px var(--shadow)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 9,
                      marginBottom: 8,
                    }}
                  >
                    <svg
                      width="17"
                      height="17"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.9"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ color: "var(--accent)" }}
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3 2" />
                    </svg>
                    <h3
                      style={{
                        fontWeight: 700,
                        fontSize: 15.5,
                        margin: 0,
                        color: "var(--ink)",
                      }}
                    >
                      Ordinačné hodiny
                    </h3>
                  </div>
                  {formattedHours.map((h, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 12,
                        padding: "9px 10px",
                        borderRadius: 9,
                        fontSize: 14,
                        ...(h.isToday
                          ? { background: "var(--accent-tint)" }
                          : {
                              borderTop:
                                i === 0 ? "none" : "1px solid var(--line)",
                            }),
                      }}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          color: "var(--ink)",
                          fontWeight: h.isToday ? 700 : 600,
                        }}
                      >
                        {h.isToday && (
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: "50%",
                              background: "var(--accent)",
                              flexShrink: 0,
                              display: "inline-block",
                            }}
                          />
                        )}
                        {h.day}
                      </span>
                      <span
                        style={{
                          color: h.isToday ? "var(--ink)" : "var(--ink-soft)",
                          fontWeight: h.isToday ? 700 : 400,
                        }}
                      >
                        {h.time}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* FOOTER */}
          <footer
            className="cp-footer"
            style={{
              marginTop: "clamp(34px,6%,52px)",
              padding: "22px 0 28px",
              borderTop: "1px solid var(--line)",
            }}
          >
            <div style={{ color: "var(--ink-mute)", fontSize: 12 }}>
              {clinic.name}
              {clinic.website ? ` · ${clinic.website}` : ""}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                color: "var(--ink-mute)",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <span>Rezervácie zabezpečuje</span>
              <a
                href="https://ordinex.sk"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  src="/ordinex-logo.svg"
                  alt="Ordinex"
                  style={{ height: 44, width: "auto", display: "block" }}
                />
              </a>
            </div>
          </footer>
        </div>
      </div>
    </>
  );
}
